import { isDemoTenant } from './clinic-preview.svelte';
import { clinicInitials, orgBrand, orgLabel } from './org-branding';

export type TenantBranding = {
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  nombreComercial?: string | null;
  dominio?: string | null;
};

export type BrandMark = {
  kind: 'logo' | 'avatar';
  logoUrl?: string;
  initials: string;
  emoji: string;
  color: string;
  accentColor: string;
  orgLabel: string;
};

export const TOSCANA_LOGO = 'https://www.toscanasystems.com/assets/img/logo.png';
export const TOSCANA_NAME = 'Toscana Salud';

const DEFAULT_BRAND = '#2563eb';
const DEFAULT_BRAND_STRONG = '#1d4ed8';
const DEFAULT_BRAND_DEEP = '#1e40af';
const DEFAULT_BRAND_SOFT = '#dbeafe';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mix(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function toHex({ r, g, b }: { r: number; g: number; b: number }) {
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function paletteFromPrimary(primary: string) {
  const base = hexToRgb(primary);
  if (!base) return null;
  const white = { r: 255, g: 255, b: 255 };
  return {
    brand: toHex(base),
    strong: toHex(mix(base, { r: 0, g: 0, b: 0 }, 0.18)),
    deep: toHex(mix(base, { r: 0, g: 0, b: 0 }, 0.38)),
    soft: toHex(mix(base, white, 0.88)),
  };
}

export function hasCustomLogo(
  b: TenantBranding | null | undefined,
  tenantId?: string | null,
): boolean {
  const url = b?.logoUrl?.trim();
  if (!url) return false;
  if (url.includes('toscanasystems.com/assets/img/logo')) return false;
  if (tenantId && isDemoTenant(tenantId)) return !!url;
  return true;
}

export function resolveLogoUrl(b: TenantBranding | null | undefined): string | null {
  if (hasCustomLogo(b)) return b!.logoUrl!.trim();
  return null;
}

export function resolveBrandMark(
  b: TenantBranding | null | undefined,
  tenantId: string,
  tenantName: string,
  orgTipo: string | null | undefined,
): BrandMark {
  const org = orgBrand(orgTipo);
  const display = b?.nombreComercial?.trim() || tenantName;
  const customLogo = hasCustomLogo(b, tenantId);

  if (customLogo && b?.logoUrl) {
    return {
      kind: 'logo',
      logoUrl: b.logoUrl.trim(),
      initials: clinicInitials(display),
      emoji: org.emoji,
      color: b.primaryColor ?? org.primary,
      accentColor: b.accentColor ?? org.accent,
      orgLabel: orgLabel(orgTipo),
    };
  }

  if (isDemoTenant(tenantId)) {
    return {
      kind: 'logo',
      logoUrl: TOSCANA_LOGO,
      initials: clinicInitials(display),
      emoji: org.emoji,
      color: b?.primaryColor ?? org.primary,
      accentColor: org.accent,
      orgLabel: orgLabel(orgTipo),
    };
  }

  return {
    kind: 'avatar',
    initials: clinicInitials(display),
    emoji: org.emoji,
    color: b?.primaryColor ?? org.primary,
    accentColor: b?.accentColor ?? org.accent,
    orgLabel: orgLabel(orgTipo),
  };
}

export function resolveDisplayName(
  b: TenantBranding | null | undefined,
  tenantNombre: string | null | undefined,
  tenantId: string,
): string {
  return b?.nombreComercial?.trim() || tenantNombre?.trim() || tenantId;
}

/** Aplica paleta completa: marca, sidebar, texto. */
export function applyTenantBranding(
  b: TenantBranding | null | undefined,
  orgTipo?: string | null,
): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Colores de marca desde backend
  const primary = b?.primaryColor?.trim() || orgBrand(orgTipo).primary;
  const palette = paletteFromPrimary(primary);
  const brand = palette?.brand ?? DEFAULT_BRAND;
  const strong = palette?.strong ?? DEFAULT_BRAND_STRONG;
  const deep = palette?.deep ?? DEFAULT_BRAND_DEEP;
  const soft = palette?.soft ?? DEFAULT_BRAND_SOFT;
  root.style.setProperty('--brand', brand);
  root.style.setProperty('--brand-strong', strong);
  root.style.setProperty('--brand-deep', deep);
  root.style.setProperty('--brand-soft', soft);

  const accent = b?.accentColor?.trim() || orgBrand(orgTipo).accent;
  if (hexToRgb(accent)) {
    root.style.setProperty('--info', accent);
  }

  // Sidebar colors (desde localStorage si no vienen del backend)
  const stored = loadLocalBranding();
  const sidebarBg = (b as any)?.sidebarColor || stored.sidebarColor || '#0c3d3a';
  const sidebarText = (b as any)?.sidebarTextColor || stored.sidebarTextColor || '#ffffff';
  root.style.setProperty('--sidebar-bg', sidebarBg);
  root.style.setProperty('--sidebar-text', sidebarText);
  root.style.setProperty('--brand-on', '#ffffff');

  // Colores de app extendidos desde localStorage
  if (stored.textColor) root.style.setProperty('--brand-deep', stored.textColor);
  if (stored.bgColor) root.style.setProperty('--app-bg', stored.bgColor);
  if (stored.sidebarActiveBg) root.style.setProperty('--sidebar-active-bg', stored.sidebarActiveBg);
  if (stored.sidebarHoverBg) root.style.setProperty('--sidebar-hover-bg', stored.sidebarHoverBg);
  if (stored.navbarBg) root.style.setProperty('--navbar-bg', stored.navbarBg);
  if (stored.navbarText) root.style.setProperty('--navbar-text', stored.navbarText);
}

export function clearTenantBranding(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const k of ['--brand', '--brand-strong', '--brand-deep', '--brand-soft', '--info', '--sidebar-bg', '--sidebar-text', '--brand-on', '--app-text', '--app-bg']) {
    root.style.removeProperty(k);
  }
}

/** Branding extendido guardado localmente */
export type LocalBrandingExt = {
  logoUrl?: string; nombreComercial?: string;
  primaryColor?: string; accentColor?: string;
  bgColor?: string; textColor?: string;
  sidebarColor?: string; sidebarTextColor?: string;
  sidebarActiveBg?: string; sidebarHoverBg?: string;
  navbarBg?: string; navbarText?: string;
  profileImage?: string;
  horario?: string; direccion?: string; telefono?: string; email?: string; descripcion?: string;
};

const LOCAL_KEY = 'toscana.branding.v1';

export function saveLocalBranding(ext: LocalBrandingExt): void {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(ext)); } catch {}
}

export function loadLocalBranding(): LocalBrandingExt {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export async function readImageAsDataUrl(file: File, maxBytes = 180_000): Promise<string> {
  if (file.size > maxBytes) {
    throw new Error(`La imagen supera ${Math.round(maxBytes / 1024)} KB. Usa PNG/JPG más pequeño o una URL externa.`);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('No se pudo leer la imagen.'));
    reader.readAsDataURL(file);
  });
}
